<template>
  <!-- Hand-rolled fixed <aside> drawer — NOT PrimeVue Drawer.
       z-index 1040: above FABs (1000) but below PrimeVue Dialog overlays (~1100+)
       so ui_action modals (ClienteModal/PDVCheckoutModal) stack above automatically. -->

  <!-- P1 — Overlay scrim for narrow viewports (<1280px, squeeze NOT active).
       Shown only via CSS @media so it is invisible in squeeze mode (≥1280px)
       where the sidebar coexists beside the content without obscuring it. -->
  <div
    v-if="isVisible"
    class="assistant-sidebar__backdrop"
    aria-hidden="true"
    @click="handleClose"
  ></div>

  <aside
    v-if="isVisible"
    data-testid="assistant-sidebar"
    class="assistant-sidebar"
    :class="{ 'assistant-sidebar--expanded': isExpanded }"
  >
    <!-- Header -->
    <div class="assistant-sidebar__header">
      <div class="assistant-sidebar__title">
        <i class="pi pi-sparkles" aria-hidden="true"></i>
        <span>{{ t('Assistente') }}</span>
      </div>
      <div class="assistant-sidebar__actions">
        <button
          data-testid="assistant-sidebar-new-chat"
          class="assistant-sidebar__icon-btn"
          :title="t('Nova conversa')"
          :aria-label="t('Nova conversa')"
          @click="handleCreate"
        >
          <i class="pi pi-plus"></i>
        </button>
        <button
          data-testid="assistant-sidebar-history-btn"
          class="assistant-sidebar__icon-btn"
          :title="t('Histórico')"
          :aria-label="t('Histórico')"
          @click="toggleHistory"
        >
          <i class="pi pi-history"></i>
        </button>
        <button
          data-testid="assistant-sidebar-expand"
          class="assistant-sidebar__icon-btn"
          :title="isExpanded ? t('Recolher') : t('Expandir')"
          :aria-label="isExpanded ? t('Recolher') : t('Expandir')"
          @click="toggleExpanded"
        >
          <!-- Single <i> with modifier-only class swap avoids unmount/remount
               mid-transition that leaves a blank box during the 0.25s width animation.
               pi-compress does NOT exist in primeicons; pi-window-minimize is the valid collapse glyph. -->
          <i class="pi" :class="isExpanded ? 'pi-window-minimize' : 'pi-expand'"></i>
        </button>
        <button
          data-testid="assistant-sidebar-close"
          class="assistant-sidebar__icon-btn"
          :title="t('Fechar')"
          :aria-label="t('Fechar')"
          @click="handleClose"
        >
          <i class="pi pi-times"></i>
        </button>
      </div>
    </div>
    <div class="assistant-sidebar__divider"></div>

    <!-- Credit chip row -->
    <div class="assistant-sidebar__chip-row">
      <AssistantCreditChip />
    </div>

    <!-- Low-balance notice — lives HERE, not in the global banner area
         (user decision 2026-08-12: credit warnings belong to the assistant;
         the global banners are subscription/grace territory). -->
    <div
      v-if="lowBalance"
      class="assistant-sidebar__low-balance"
      data-testid="assistant-low-balance-notice"
      role="status"
    >
      <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
      <span>{{ t('Saldo de créditos baixo.') }}</span>
    </div>

    <!-- Content: history panel overlay OR thread -->
    <div class="assistant-sidebar__content">
      <!-- History panel (overlay) -->
      <AssistantHistoryPanel
        v-if="showHistory"
        :chats="showArchived ? archivedChats : chats"
        :active-chat-id="activeChatId"
        :loading="chatsLoading"
        :archived-view="showArchived"
        @create="handleCreate"
        @select="handleHistorySelect"
        @rename="handleRename"
        @delete="handleDelete"
        @archive="handleArchive"
        @toggle-archived="handleToggleArchived"
        @close="showHistory = false"
      />

      <!-- Thread -->
      <AssistantThread
        v-else
        :messages="messages"
        :loading="threadLoading"
        :error="error"
        :active-chat-id="activeChatId"
        :insufficient="walletState === 'insufficient'"
        @send="send"
      />
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import AssistantCreditChip from './AssistantCreditChip.vue';
import AssistantThread from './AssistantThread.vue';
import AssistantHistoryPanel from './AssistantHistoryPanel.vue';
import { useChats } from '../../composables/useChats';
import { useAgentChat } from '../../composables/useAgentChat';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { useCreditWallet } from '../../composables/useCreditWallet';
import { pickDefaultChat } from './pickDefaultChat';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';

const { t } = useI18n();

const isVisible = ref<boolean>(false);
const isExpanded = ref<boolean>(false);
const showArchived = ref<boolean>(false);
const showHistory = ref<boolean>(false);

// D10/D11/D12: sync html-level classes + --assistant-sidebar-width CSS var so
// the shell (.app-main) and PrimeVue dialog masks can react to sidebar state
// without cross-tree Vue plumbing. The html-class approach matches the pattern
// suggested in the D10/D11/D12 contract and keeps AssistantSidebar self-contained.
//
// Topbar stays full-width (position:fixed, outside .app-main) — materially
// simpler per D10 contract.
//
// D12: isExpanded → fullscreen overlay (entire viewport incl. topbar).
// In fullscreen the squeeze + mask constraints are disabled via CSS
// :not(.assistant-sidebar-expanded) guards in theme.css.
watch(
  [isVisible, isExpanded],
  ([visible, expanded]) => {
    const html = document.documentElement;
    if (visible && !expanded) {
      html.classList.add('assistant-sidebar-open');
      html.classList.remove('assistant-sidebar-expanded');
      html.style.setProperty('--assistant-sidebar-width', '28rem');
    } else if (visible && expanded) {
      html.classList.add('assistant-sidebar-open');
      html.classList.add('assistant-sidebar-expanded');
      html.style.removeProperty('--assistant-sidebar-width');
    } else {
      html.classList.remove('assistant-sidebar-open');
      html.classList.remove('assistant-sidebar-expanded');
      html.style.removeProperty('--assistant-sidebar-width');
    }
  },
  { immediate: false },
);

const {
  chats,
  loading: chatsLoading,
  error: chatsError,
  listChats,
  createChat,
  renameChat,
  deleteChat,
  archiveChat,
  archivedChats,
  listArchivedChats,
} = useChats();

const {
  messages,
  loading: threadLoading,
  error,
  activeChatId,
  loadThread,
  send,
} = useAgentChat();

const { confirmAction } = useConfirmAction();
const { selectedFranquiaId, fetchWallet, state: walletState, lowBalance } = useCreditWallet();

function onTurnDone(): void {
  if (selectedFranquiaId.value !== null) {
    void fetchWallet(selectedFranquiaId.value);
  }
}

function onCreditsInsufficient(): void {
  walletState.value = 'insufficient';
}

const handleOpen = (): void => {
  log('AssistantSidebar', 'handleOpen', 'Opening assistant sidebar');
  // Mutual exclusion: closing the modal when the sidebar opens (D2).
  eventBus.emit('modal.assistant.close');
  isVisible.value = true;
  eventBus.on('agent.turn.done', onTurnDone);
  eventBus.on('agent.credits.insufficient', onCreditsInsufficient);
  void listChats()
    .then(() => {
      const defaultId = pickDefaultChat(chats.value);
      if (defaultId !== null) {
        return loadThread(defaultId);
      }
    })
    .catch((err) => {
      // listChats/loadThread already surface their own error state in the UI;
      // a failed auto-open must not escape as an unhandled rejection.
      log('AssistantSidebar', 'handleOpen', 'Falha ao abrir a conversa padrão', err);
    });
};

const handleClose = (): void => {
  log('AssistantSidebar', 'handleClose', 'Closing assistant sidebar');
  isVisible.value = false;
  isExpanded.value = false;
  showArchived.value = false;
  showHistory.value = false;
  eventBus.off('agent.turn.done', onTurnDone);
  eventBus.off('agent.credits.insufficient', onCreditsInsufficient);
};

function toggleExpanded(): void {
  isExpanded.value = !isExpanded.value;
}

function toggleHistory(): void {
  showHistory.value = !showHistory.value;
  if (showHistory.value && !showArchived.value) {
    void listChats();
  }
}

/**
 * handleCreate — the + button and the history panel's "nova conversa" both land here.
 *
 * S7-R01 made the paying franchise mandatory on POST /chats. `selectedFranquiaId`
 * is only populated once the credit chip has fetched a wallet, which never happens
 * for groups that don't render the chip (Operador / Atendente) and hasn't happened
 * yet for anyone whose first click beats the fetch. Posting in that state can only
 * come back 400 billing_franquia_required, so instead of failing we ASK: the
 * franchise modal writes selectedFranquiaId, and the user clicks + again.
 */
async function handleCreate(): Promise<void> {
  showHistory.value = false;

  if (selectedFranquiaId.value === null) {
    log('AssistantSidebar', 'handleCreate', 'Sem franquia pagadora — abrindo seletor');
    eventBus.emit('modal.assistant-franchise.open');
    return;
  }

  try {
    const chat = await createChat(selectedFranquiaId.value);
    await loadThread(chat.id);
  } catch (err) {
    log('AssistantSidebar', 'handleCreate', 'Falha ao criar conversa', err);
    // useChats maps the named backend codes (billing_franquia_required, 401) to
    // PT; only fall back to the generic line when it had nothing more specific.
    eventBus.emit(
      'globalMessage.error',
      chatsError.value ?? t('Não foi possível criar a conversa. Tente novamente.'),
    );
  }
}

async function handleHistorySelect(chatId: number): Promise<void> {
  if (chatId !== activeChatId.value) {
    await loadThread(chatId);
  }
  showHistory.value = false;
}

async function handleRename(chatId: number, title: string): Promise<void> {
  await renameChat(chatId, title);
  log('AssistantSidebar', 'handleRename', 'Conversa renomeada', { chatId });
}

function handleDelete(chatId: number): void {
  confirmAction({
    header: t('Excluir conversa'),
    message: t('Tem certeza que deseja excluir esta conversa?'),
    acceptLabel: t('Excluir'),
    rejectLabel: t('Cancelar'),
    onAccept: () => {
      void removeChat(chatId);
    },
  });
}

async function removeChat(chatId: number): Promise<void> {
  await deleteChat(chatId);
  if (chatId === activeChatId.value) {
    activeChatId.value = null;
    messages.value = [];
  }
  log('AssistantSidebar', 'removeChat', 'Conversa excluída', { chatId });
}

async function handleArchive(chatId: number): Promise<void> {
  await archiveChat(chatId);
  if (chatId === activeChatId.value) {
    activeChatId.value = null;
    messages.value = [];
  }
  log('AssistantSidebar', 'handleArchive', 'Conversa arquivada', { chatId });
}

async function handleToggleArchived(): Promise<void> {
  showArchived.value = !showArchived.value;
  if (showArchived.value) {
    await listArchivedChats();
  } else {
    await listChats();
  }
}

onMounted(() => {
  eventBus.on('sidebar.assistant.open', handleOpen);
  eventBus.on('sidebar.assistant.close', handleClose);
});

onBeforeUnmount(() => {
  eventBus.off('sidebar.assistant.open', handleOpen);
  eventBus.off('sidebar.assistant.close', handleClose);
  eventBus.off('agent.turn.done', onTurnDone);
  eventBus.off('agent.credits.insufficient', onCreditsInsufficient);
  const html = document.documentElement;
  html.classList.remove('assistant-sidebar-open');
  html.classList.remove('assistant-sidebar-expanded');
  html.style.removeProperty('--assistant-sidebar-width');
});
</script>

<style scoped>
.assistant-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 28rem;
  background: var(--p-surface-0, #ffffff);
  border-left: 1px solid var(--p-surface-200, #e2e8f0);
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.08);
  z-index: 1040;
  display: flex;
  flex-direction: column;
  transition: width 0.25s ease;
}

/* D12: fullscreen overlay — covers entire viewport including topbar. */
.assistant-sidebar--expanded {
  width: 100vw;
  top: 0;
  right: 0;
  height: 100vh;
  z-index: 1050;
}

.assistant-sidebar__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  height: 3.25rem;
  flex-shrink: 0;
}

.assistant-sidebar__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--p-text-color, #1e293b);
}

.assistant-sidebar__title .pi-sparkles {
  color: var(--p-primary-color, #2563eb);
  font-size: 1rem;
}

.assistant-sidebar__actions {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.assistant-sidebar__icon-btn {
  background: none;
  border: none;
  border-radius: 0.375rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--p-text-muted-color, #64748b);
  transition: background 0.15s, color 0.15s;
  padding: 0;
}

.assistant-sidebar__icon-btn:hover {
  background: var(--p-surface-100, #f1f5f9);
  color: var(--p-text-color, #1e293b);
}

.assistant-sidebar__low-balance {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 12px 8px;
  padding: 6px 10px;
  border-radius: 6px;
  background: #fff3cd;
  color: #664d03;
  border: 1px solid #ffe69c;
  font-size: 13px;
}

.assistant-sidebar__divider {
  height: 1px;
  background: var(--p-surface-200, #e2e8f0);
  flex-shrink: 0;
}

.assistant-sidebar__chip-row {
  padding: 0.5rem 0.75rem;
  flex-shrink: 0;
}

.assistant-sidebar__content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 0.75rem 0.75rem;
}

/* P1 — Scrim backdrop for overlay mode (<1280px, where squeeze is NOT active).
   Hidden at ≥1280px so it never appears when the sidebar squeezes the layout. */
.assistant-sidebar__backdrop {
  display: none;
}

@media (max-width: 1279.98px) {
  .assistant-sidebar__backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    /* z-index 1039 — just below the sidebar (1040) so the scrim sits behind
       the drawer but above all page content. */
    z-index: 1039;
  }
}

/* P2 — Constrain expanded (fullscreen) content to a centered 760px column
   so the layout reads as a focused reading column rather than edge-to-edge.
   The header row uses the same max-width so its actions align with the column. */
.assistant-sidebar--expanded .assistant-sidebar__header {
  max-width: 760px;
  margin-inline: auto;
  width: 100%;
}

.assistant-sidebar--expanded .assistant-sidebar__chip-row,
.assistant-sidebar--expanded .assistant-sidebar__content {
  max-width: 760px;
  margin-inline: auto;
  width: 100%;
}
</style>
